import { useState, type FormEvent } from "react"
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react"
import { useLang } from "../context/LangContext"
import { supabase } from "../lib/supabaseClient"

const T = {
  en: {
    title: "Contact Us",
    sub: "We'd love to hear from you. Fill out the form and our team will get back to you as soon as possible.",
    fname: "First name", lname: "Last name",
    email: "Email address", subject: "Subject", message: "Your message",
    send: "Send message", sending: "Sending…",
    success: "Message sent! We'll be in touch soon.",
    error: "Something went wrong. Please email us directly at info@ucloud360.com",
    where: "Where we are", call: "Call us", write: "Write to us",
    subjects: ["General inquiry", "Partnership", "Technical support", "Membership", "Press", "Other"],
  },
  es: {
    title: "Contáctanos",
    sub: "Nos encantaría saber de ti. Completa el formulario y nuestro equipo te responderá lo antes posible.",
    fname: "Nombre", lname: "Apellido",
    email: "Correo electrónico", subject: "Asunto", message: "Tu mensaje",
    send: "Enviar mensaje", sending: "Enviando…",
    success: "¡Mensaje enviado! Te contactaremos pronto.",
    error: "Algo salió mal. Por favor escríbenos directamente a info@ucloud360.com",
    where: "Dónde estamos", call: "Llámanos", write: "Escríbenos",
    subjects: ["Consulta general", "Alianzas", "Soporte técnico", "Membresía", "Prensa", "Otro"],
  },
}

export default function ContactPage() {
  const { lang } = useLang()
  const t = T[lang]

  const [form, setForm] = useState({ fname: "", lname: "", email: "", subject: "", message: "" })
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "err">("idle")

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setStatus("sending")
    try {
      const { error } = await supabase.from("contact_requests").insert([{
        first_name: form.fname,
        last_name: form.lname,
        email: form.email,
        subject: form.subject,
        message: form.message,
        lang,
        created_at: new Date().toISOString(),
      }])
      if (error) throw error
      setStatus("ok")
      setForm({ fname: "", lname: "", email: "", subject: "", message: "" })
    } catch {
      setStatus("err")
    }
  }

  const inputCls = "w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-900 px-4 py-3 text-sm text-black dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#2563EB] dark:focus:border-[#60a5fa] transition"

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
      {/* Hero */}
      <section className="bg-[#F8FAFF] dark:bg-neutral-900/50 border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h1 className="text-4xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-3 text-gray-500 dark:text-gray-400 max-w-xl">{t.sub}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-12">

        {/* Contact info */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center gap-2 text-[#2563EB] mb-2">
              <MapPin className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t.where}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Miami, FL 33178<br />United States
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-[#2563EB] mb-2">
              <Phone className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t.call}</span>
            </div>
            <a href="tel:+17864514979" className="text-sm text-gray-600 dark:text-gray-300 hover:text-[#2563EB] transition">
              (786) 451-4979
            </a>
          </div>

          <div>
            <div className="flex items-center gap-2 text-[#2563EB] mb-2">
              <Mail className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t.write}</span>
            </div>
            <a href="mailto:info@ucloud360.com" className="text-sm text-gray-600 dark:text-gray-300 hover:text-[#2563EB] transition">
              info@ucloud360.com
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-2">
          {status === "ok" ? (
            <div className="rounded-2xl border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30 p-8 text-center">
              <div className="text-2xl mb-3">✓</div>
              <p className="text-green-700 dark:text-green-300 font-medium">{t.success}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t.fname}</label>
                  <input value={form.fname} onChange={e => set("fname", e.target.value)} required className={inputCls} placeholder="Jean Paul" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t.lname}</label>
                  <input value={form.lname} onChange={e => set("lname", e.target.value)} className={inputCls} placeholder="Zerpa" />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t.email}</label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required className={inputCls} placeholder="you@company.com" />
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t.subject}</label>
                <select value={form.subject} onChange={e => set("subject", e.target.value)} required className={inputCls}>
                  <option value="">—</option>
                  {t.subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">{t.message}</label>
                <textarea value={form.message} onChange={e => set("message", e.target.value)} required rows={5} className={`${inputCls} resize-none`} />
              </div>

              {status === "err" && (
                <p className="text-sm text-red-600 dark:text-red-400">{t.error}</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1d4ed8] transition disabled:opacity-60"
              >
                {status === "sending"
                  ? <><Loader2 className="h-4 w-4 animate-spin" />{t.sending}</>
                  : <><Send className="h-4 w-4" />{t.send}</>
                }
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
